import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { BaseDialogComponent } from '../base-dialog/base-dialog.component';
import { BaseComponent } from '../base-form/base-form.component';
import { Subscription } from 'rxjs';
import { MiscEventEntityComponent } from '../misc-event-entity/misc-event-entity.component';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-package-entity',
  templateUrl: './package-entity.component.html',
  styleUrls: ['./package-entity.component.css']
})

export class PackageEntityComponent extends BaseDialogComponent implements OnInit {

  override eckType = "packages";
  override bundle = "package";
  override entityId = this._injectedDialogData['data']['EntityID'];

  showLessonTypeAndCount: boolean = true;
  private formSubscription: Subscription;
  isUpdating = false;

  isMiscOrSundry: boolean;
  abbreviationTouched = false;

  /** SPS codes whose packages carry a Package Code: Misc (554), Sundry (558), Non Unit (657). */
  static readonly PACKAGE_CODE_SPS_CODES = [554, 558, 657];

  /**
   * Whether the Package Code select is shown. Whenever it is shown it must be
   * filled in. A blank code leaves the package invisible to the reports that
   * filter by it. Mirrors the template's d-none condition.
   */
  get packageCodeShown(): boolean {
    const spsCode = Number(this.f?.form?.get('field_sps_code')?.value);
    return PackageEntityComponent.PACKAGE_CODE_SPS_CODES.includes(spsCode);
  }

  /** Package Code term ids offered for the current SPS code (the same lists the select renders). */
  allowedPackageCodes(): string[] {
    switch (Number(this.f?.form?.get('field_sps_code')?.value)) {
      case 554: return this.misc_list;
      case 558: return this.sundry_list;
      case 657: return this.non_unit_list;
      default: return [];
    }
  }

  /** True when the selected Package Code is one the current SPS code actually offers. */
  packageCodeValid(): boolean {
    const code = this.f?.form?.get('field_package_code')?.value;
    return !!code && this.allowedPackageCodes().includes(String(code));
  }

  /**
   * Called when the SPS code changes. A code picked under the previous SPS
   * code stays in the control even though the select no longer lists it, so
   * the field looks blank while validation still sees a value. Clear it.
   */
  onSpsCodeChange() {
    const control = this.f?.form?.get('field_package_code');
    if (control?.value && !this.packageCodeValid()) {
      control.patchValue(null);
    }
    this.calcNewTotal();
  }

  isFormValid(): boolean {
    if (!this.f || !this.f.form) {
      return false;
    }

    const form = this.f.form;
    const requiredFields = ['field_package_name', 'field_sps_code'];

    // Check if all required fields are filled
    const allRequiredFieldsFilled = requiredFields.every(field => form.get(field)?.value);

    // Package Code is required whenever its select is shown (Misc, Sundry, Non
    // Unit), and must be one of the codes offered for that SPS code. A stale
    // value left over from a previous SPS code doesn't count.
    const packageCodeOk = !this.packageCodeShown || this.packageCodeValid();

    // Special check for lesson price to allow 0
    const lessonPrice = form.get('field_lesson_price')?.value;
    const lessonPriceValid = lessonPrice !== null && lessonPrice !== undefined && lessonPrice !== '';

    // Check if lesson count is required and filled (allow 0 as valid)
    const lessonCountRequired = !this.isMiscOrSundry;
    const lessonCount = form.get('field_lesson_count')?.value;
    const lessonCountValid = lessonCountRequired ? (lessonCount !== null && lessonCount !== undefined && lessonCount !== '') : true;

    // A declared event package must actually reference an event, or the
    // report's event mode silently misses its payments.
    const eventLinkValid = !(this.isEventPackage && form.get('field_sps_code')?.value == 554)
      || !!form.get('field_misc_event')?.value;

    // System packages cannot be retired
    const isSystemPackage = form.get('field_is_system_package')?.value;
    const isRetired = form.get('field_retired')?.value;
    const systemPackageRetirementValid = !(isSystemPackage && isRetired);

    // Log validation state
    // LoggerService.log('Validation:', {
    //   allRequiredFieldsFilled,
    //   lessonPriceValid,
    //   lessonPrice,
    //   lessonCountValid,
    //   requiredFields,
    //   lessonCountRequired
    // });

    return allRequiredFieldsFilled && packageCodeOk && lessonPriceValid && lessonCountValid && systemPackageRetirementValid && eventLinkValid;
  }

  calcNewTotal() {
    let total;
    let formValues = this.f.form.value;

    LoggerService.log('formValues', formValues);

    // If the category is misc (554) or sundry (558), return the lesson price.
    if (formValues.field_sps_code == '554' || formValues.field_sps_code == '558') {
      // Update whatever lesson count is to 0
      this.f.form.patchValue({
        field_lesson_count: 0,
      });

      total = formValues.field_lesson_price;
    } else {
      total = formValues.field_lesson_price * formValues.field_lesson_count;
    }

    // Calculate the discount - pass the subtotal to ensure percentage is calculated correctly
    let discount = this.calcDiscount(total);

    // Apply the discount and round to cents to avoid floating-point drift
    total = Math.round((total - discount) * 100) / 100;

    // Add taxes if applicable
    // if (formValues.field_tax_percentage) {
    //   let taxAmount = (total * (formValues.field_tax_percentage / 100));
    //   total = total + taxAmount;
    // }

    this.f.form.patchValue({
      field_total_price: total
    });

    // Determine if enrollment is a misc or sundry item.
    this.isMiscOrSundry = (formValues.field_sps_code == 554 || formValues.field_sps_code == 558) ?? false;
  }

  calcDiscount(subtotal?: number) {
    let discount = 0;
    let formValues = this.f.form.value;

    // If the discount is a percentage, calculate based on subtotal (before discount)
    if (formValues.field_discount_type == 'percentage') {
      // If subtotal is not provided, calculate it
      if (subtotal === undefined) {
        if (formValues.field_sps_code == '554' || formValues.field_sps_code == '558') {
          subtotal = formValues.field_lesson_price;
        } else {
          subtotal = formValues.field_lesson_price * formValues.field_lesson_count;
        }
      }
      discount = subtotal * (formValues.field_discount / 100);
    } else {
      discount = formValues.field_discount;
    }

    return discount;
  }

  onSubmitPackage(f: NgForm) {
    if (this.isFormValid()) {
      this.displayProgressSpinner(true);

      LoggerService.log('packageForm', this.f.value);

      // If SPS code is sundry (558), Misc (554), or Non Unit (657) don't send lesson_type.
      if ((this.f.value.field_lesson_type == '') && (this.f.value.field_sps_code == '558' || this.f.value.field_sps_code == '554' || this.f.value.field_sps_code == '657')) {
        // delete (this.f.value.field_lesson_type);
      }

      // Iterate over each field in the form
      for (let field in this.f.controls) {
        // Check if the field is an empty string
        if (this.f.control.get(field).value === '') {
          // If it is, set its value to null
          this.f.control.get(field).patchValue(null);
        }
        if (this.f.control.get(field).value === undefined) {
          // If it is, set its value to null
          this.f.control.get(field).patchValue(null);
        }
      }

      // Set default value for field_retired if nothing is set.
      if (this.f.value?.['field_retired'] == "" || this.f.value?.['field_retired'] == null || this.f.value?.['field_retired'] == undefined) {
        this.f.value.field_retired = false;
      }

      if (this.f.value.field_lesson_type == undefined) {
        this.f.value.field_lesson_type = null;
      }

      // Only misc packages declared as event packages carry an event link.
      if (this.f.value.field_sps_code != 554 || !this.isEventPackage) {
        this.f.value.field_misc_event = null;
      }

      this.onSubmit(this.f)
    }
  }

  getAmountLabel(): string {
    const spsCode = this.f?.form?.value?.field_sps_code;
    switch (spsCode) {
      case '554': // MISC
        return 'Event Amount';
      case '558': // SUNDRY
        return 'Item Amount';
      default:
        return 'Lesson Amount';
    }
  }

  onAbbreviationFocus() {
    this.abbreviationTouched = true;
  }

  isFieldLocked(): boolean {
    const isSystemPackage = this.f?.form?.value?.field_is_system_package;
    return !!isSystemPackage && !this._permissionsService.isSuperAdmin();
  }

  generateAbbreviation(name: string): string {
    // Split the name into words
    const words = name.split(' ');

    if (words.length >= 3) {
      // If there are 3 or more words, take the first letter of the first three words
      return words.slice(0, 3).map(word => word[0].toUpperCase()).join('');
    } else if (words.length === 2) {
      // If there are 2 words, take the first letter of the first word and the first two letters of the second word
      return (words[0][0] + words[1].slice(0, 2)).toUpperCase();
    } else if (words.length === 1 && words[0].length >= 3) {
      // If there's only one word, take the first three letters
      return words[0].slice(0, 3).toUpperCase();
    } else {
      // If the name is too short, pad with 'X's
      return (words[0] + 'XXX').slice(0, 3).toUpperCase();
    }
  }

  onPackageNameChange(newValue: string, abbreviationInput: NgModel) {
    // If package is being edited, don't generate an abbreviation.
    if (this.actionType != "create") {
      return;
    }

    if (!this.abbreviationTouched && abbreviationInput.untouched) {
      const abbreviation = this.generateAbbreviation(newValue);
      this.f.form.patchValue({
        field_abbreviation: abbreviation
      });
    }
  }

  /**
   * AMI's generic DOR codes and their display labels, keyed by the AMI code
   * that starts each term name ("MISC_39 - Area 1 DOR"). They are matched by
   * that code rather than by term id because the update hook that creates
   * them assigns whatever ids are free on each site.
   *
   * MISC_02 (Area 6) is not listed here: the Seattle term (161) has carried
   * that code since the start and is labelled "DOR - Area 6" by id below.
   */
  static readonly MISC_AMI_CODE_LABELS: Record<string, string> = {
    "MISC_39": "DOR - Area 1",
    "MISC_40": "DOR - Area 2",
    "MISC_41": "DOR - Area 3",
    "MISC_42": "DOR - Area 4",
    "MISC_43": "DOR - Area 5",
    "MISC_44": "DOR - Area 7",
    "MISC_38": "DOR - Area 8",
    "MISC_45": "DOR - APAC",
  }

  private miscListCache: { key: string; ids: string[] } | null = null;

  /** Misc package code term ids: the fixed ids plus the AMI-code matches above. */
  get misc_list(): string[] {
    const packageCodes: any[] = this._taxonomyService?.package_code ?? [];
    const key = String(packageCodes.length);
    if (this.miscListCache?.key !== key) {
      const amiCodeIds = packageCodes
        .filter((element: any) => this.amiCodeLabel(element) !== undefined)
        .map((element: any) => String(element.id));
      this.miscListCache = { key, ids: [...new Set([...this.misc_term_ids, ...amiCodeIds])] };
    }
    return this.miscListCache.ids;
  }

  private amiCodeLabel(element: { name?: string }): string | undefined {
    const amiCode = String(element?.name ?? '').split(' - ')[0].trim();
    return PackageEntityComponent.MISC_AMI_CODE_LABELS[amiCode];
  }

  misc_term_ids = [
    "1401",
    "160",
    "161",
    "162",
    "165",
    "166",
    "167",
    "170",
    "172",
    "173",
    "174",
    "175",
    "1402",
    "177",
    "180",
    "181",
    "182",
    "183",
    "188",
    "190",
    "191",
    "193",
    "636",
    "637",
    "1400",
  ]

  non_unit_list = [
    "173",
    // "1399",
    // "1397",
    // "1398",
    // "1396"
  ]

  non_unit_group_non_list = [
    "1410",
    "1411",
    // "1397",
    // "1398",
    // "1396"
  ]

  sundry_list = [
    "196",
    "197",
    "198",
    "199",
    "200",
    "201",
    "202",
    "203",
    "195",
    "635"
  ]

  packageCodeLabelOverrides: Record<string, string> = {
    "160": "DOR Australasian - Area 10",
    "161": "DOR - Area 6",
    "162": "DOR Boston - Area 1",
    "165": "DOR Ciao Amore - Area 8",
    "166": "Showcase (Combined)",
    "167": "Embassy Ball",
    "170": "DOR HulaRama - Area 6/7",
    "172": "DOR Northstar - Area 4",
    "173": "Party Time/NonUnit",
    "174": "DOR AMI Superama",
    "175": "DOR Unique - Area 7",
    "1402": "DOR Holiday Ball - Area 2",
    "177": "DOR AMI World",
    "180": "DOR San Francisco - Area 6/7",
    "181": "DOR Banff - Area 6",
    "182": "DOR Portland - Area 6",
    "183": "Medal Ball",
    "188": "Showcase (Independent)",
    "190": "DOR Texas - Area 5",
    "191": "Blackpool Dance Festival",
    "193": "DOR FlamingoRama - Area 3",
    "636": "Virtual Dance Challenge",
    "637": "DOR Beach-O-Rama"
  }

  packageCodeOptionsCache: Record<string, { id: string; name: string; displayName: string }[]> = {};




  // The studio's misc_event occurrences, for the optional Event reference on
  // misc packages. [{id, title}] parsed from the collapsed entity response.
  miscEvents: { id: string; title: string; systemWide?: boolean }[] = [];

  // UI state for the "Event package" yes/no. Not a stored field; derived from
  // field_misc_event on edit, and gates whether the Event select is shown and
  // required. Unchecking clears the link on save.
  isEventPackage = false;

  onEventPackageToggle(checked: boolean) {
    this.isEventPackage = checked;
    if (!checked && this.f?.form) {
      this.f.form.patchValue({ field_misc_event: null });
    }
  }

  override ngOnInit(): void {
    let action = this._injectedDialogData?.['data']?.['action'];

    if (action) {
      this.initActionType = this._injectedDialogData['data']['action'];
      this.actionType = this._injectedDialogData['data']['action'];
    }

    LoggerService.log('this._injectedDialogData', this._injectedDialogData);
    this.checkIfEventPassedCurrentTime();

    this.initFormSubscription();
    this.loadMiscEvents();
  }

  /**
   * Load the current studio's misc_event entities for the Event dropdown.
   * The generic ECK endpoint scopes the list to the studio server-side.
   */
  loadMiscEvents(onLoaded?: () => void) {
    this._drupalRESTService.httpGET('/api/misc-event/list')
      .subscribe((data: any) => {
        this.miscEvents = this.parseMiscEvents(data);
        onLoaded?.();
      });
  }

  private parseMiscEvents(data: any): { id: string; title: string; systemWide: boolean }[] {
    return (data ?? []).map((event: any) => ({
      id: event?.id?.toString(),
      title: event?.title ?? '(untitled event)',
      systemWide: !!event?.system_wide,
    })).filter((event: any) => event.id);
  }

  get franchiseEvents() {
    return this.miscEvents.filter(event => event.systemWide);
  }

  get studioEvents() {
    return this.miscEvents.filter(event => !event.systemWide);
  }

  /**
   * Create an event without leaving the package form. Package creation is
   * where the need for an event usually surfaces. The new event is selected
   * automatically once saved.
   */
  openCreateEventDialog(): void {
    this._dialogService.openDialog(MiscEventEntityComponent, "defaultWithData", {
      data: {
        EntityID: null,
        eckType: 'misc_event',
        bundle: 'misc_event',
        action: 'create',
        fieldsData: { field_package_code: this.f?.form?.value?.field_package_code },
      },
    }).afterClosed().subscribe(created => {
      // Create returns the collapsed entity shape (id: [{value}]); the row
      // shape used elsewhere returns a flat string id. Accept either.
      const rawId = created?.['id'];
      const newId = (Array.isArray(rawId) ? rawId[0]?.value : rawId)?.toString();
      if (!newId) { return; }
      // Reload through the list endpoint, not the raw ECK entity endpoint:
      // the raw shape wraps title/id in field arrays, which rendered every
      // option as "[object Object]" and dropped the franchise/studio split.
      // Patch after the reloaded options have rendered. mat-select resolves a
      // patched value against the options currently in the DOM, so selecting
      // in the same tick as the list assignment silently drops the value and
      // leaves the field blank - which reads as "it didn't save" and gets the
      // event created a second time.
      this.loadMiscEvents(() => {
        setTimeout(() => {
          this.isEventPackage = true;
          this.f?.form?.patchValue({ field_misc_event: newId });
        });
      });
    });
  }

  override setFormValues(formRef = null) {
    // Call the parent setFormValues first
    super.setFormValues(formRef);

    // Explicitly handle field_retired boolean conversion
    if (this.entityData && this.entityData.field_retired && this.f?.form) {
      const retiredValue = this.entityData.field_retired;
      const booleanValue = (retiredValue === "1" || retiredValue === 1) ? true : false;

      // Patch the form with the correct boolean value
      this.f.form.patchValue({
        field_retired: booleanValue
      });
    }

    // Explicitly sync the Event reference: the collapsed entity may carry the
    // reference as a nested entity object, a delta array, or a bare id.
    if (this.entityData && this.f?.form) {
      const raw = this.entityData.field_misc_event;
      const eventId = raw?.id ?? raw?.[0]?.target_id ?? raw?.target_id ?? (typeof raw === 'object' ? null : raw);
      this.isEventPackage = !!eventId;
      this.f.form.patchValue({
        field_misc_event: eventId ? String(eventId) : null,
      });
    }

    // Drupal DECIMAL(10,2) returns "0.50" / "1.00", but mat-option values are "0.5" / "1".
    // Without this normalization the select won't match and the field appears blank on edit.
    if (this.entityData && this.entityData.field_units != null && this.f?.form) {
      const normalized = parseFloat(this.entityData.field_units).toString();
      this.f.form.patchValue({
        field_units: normalized
      });
    }
  }

  private initFormSubscription() {
    LoggerService.log('Attempting to initialize form subscription');
    if (this.f && this.f.form) {
      LoggerService.log('Form found, setting up subscription');
      this.formSubscription = this.f.form.valueChanges.subscribe((formValues) => {
        LoggerService.log('Form values changed:', formValues);
        this.updateFieldVisibility();
      });
    } else {
      LoggerService.warn('Form not available for subscription');
      setTimeout(() => this.initFormSubscription(), 100);
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    LoggerService.log('PackageEntityComponent destroyed');
    this.unsubscribeForm();
  }

  private unsubscribeForm() {
    if (this.formSubscription) {
      LoggerService.log('Unsubscribing from form changes');
      this.formSubscription.unsubscribe();
      this.formSubscription = null;
    }
  }

  private updateFieldVisibility() {
    if (this.isUpdating) {
      LoggerService.log('Update already in progress, skipping');
      return;
    }

    this.isUpdating = true;

    if (!this.f || !this.f.form) {
      LoggerService.warn('Form not available for updateFieldVisibility');
      this.isUpdating = false;
      return;
    }

    const spsCode = this.f.form.get('field_sps_code')?.value;
    const previousShowFields = this.isMiscOrSundry;
    this.isMiscOrSundry = (spsCode == '554' || spsCode == '558') ?? false;

    LoggerService.log(`SPS Code: ${spsCode}, Show Lesson Type and Count: ${this.isMiscOrSundry}`);

    if (previousShowFields !== this.isMiscOrSundry) {
      LoggerService.log(`Field visibility changed from ${previousShowFields} to ${this.isMiscOrSundry}`);

      if (this.isMiscOrSundry) {
        const previousLessonType = this.f.form.get('field_lesson_type')?.value;
        const previousLessonCount = this.f.form.get('field_lesson_count')?.value;

        if (previousLessonType !== null || previousLessonCount !== null) {
          LoggerService.log(`Clearing Lesson Type and Count fields. Previous values: Type=${previousLessonType}, Count=${previousLessonCount}`);
          this.f.form.patchValue({
            field_lesson_type: null,
            field_lesson_count: null
          }, { emitEvent: false });
        }
      }
    }

    this.isUpdating = false;
  }

  getPackageCodeOptions(filterIds: string[]) {
    const packageCodes = this._taxonomyService?.package_code ?? [];
    if (!packageCodes.length) {
      return [];
    }

    const cacheKey = `${filterIds.join(',')}::${packageCodes.length}`;
    if (!this.packageCodeOptionsCache[cacheKey]) {
      this.packageCodeOptionsCache[cacheKey] = this.buildPackageCodeOptions(packageCodes, filterIds);
    }

    return this.packageCodeOptionsCache[cacheKey];
  }

  private buildPackageCodeOptions(packageCodes: any[], filterIds: string[]) {
    return packageCodes
      .filter((element: any) => filterIds.includes(element.id))
      .map((element: any) => ({
        id: element.id,
        name: element.name,
        displayName: this.getPackageCodeDisplayName(element),
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  private getPackageCodeDisplayName(element: { id: string; name: string }) {
    return this.packageCodeLabelOverrides[element.id] ?? this.amiCodeLabel(element) ?? element.name;
  }

}
